import { useEffect, useState } from 'react'
import type { QuizQuestion, ScentFamily } from '@/types'
import { API_BASE } from '@/lib/config'

export type StorefrontSettings = {
  heroHeadline?: string
  heroSubheadline?: string
  featuredQuote?: string
  announcementText?: string
  announcementMessages?: string[]
  freeShippingThresholdKobo?: number
}

export type HeroSettings = {
  imageUrl?: string
  videoUrl?: string
  videoPosterUrl?: string
  showFilmButton?: boolean
  filmButtonText?: string
}

export type AboutCard = {
  title: string
  body: string
}

export type AboutSettings = {
  eyebrow?: string
  title?: string
  paragraphs?: string[]
  imageUrl?: string
  cards?: AboutCard[]
}

export type QuizSettings = {
  questions?: QuizQuestion[]
}

export type Testimonial = {
  quote: string
  author: string
  fragrance: string
}

export type TestimonialsSettings = {
  testimonials?: Testimonial[]
}

export type PressSettings = {
  names?: string[]
}

export type StatItem = {
  value: string
  label: string
}

export type StatsSettings = {
  stats?: StatItem[]
}

export type MarqueeSettings = {
  items?: string[]
}

export type BrandSettings = {
  logoUrl?: string
  faviconUrl?: string
  primaryColor?: string
  accentColor?: string
  instagram?: string
  twitter?: string
  facebook?: string
  email?: string
  phone?: string
  address?: string
  footerTagline?: string
}

export type NavCategory = {
  key: string
  label: string
  href: string
  visible?: boolean
}

export type BannerPromo = {
  enabled?: boolean
  text?: string
  link?: string
  style?: 'pill' | 'strip'
  background?: string
}

export type StreakPromo = {
  enabled?: boolean
  headline?: string
  body?: string
  ctaLabel?: string
}

export type MidnightMarketPromo = {
  enabled?: boolean
  title?: string
  subtitle?: string
  startHour?: number
  endHour?: number
  productSlugs?: string[]
  active?: boolean
}

export type PromoModules = {
  banner?: BannerPromo
  streak?: StreakPromo
  midnightMarket?: MidnightMarketPromo
}

export type ContentVisibility = {
  showAnnouncement?: boolean
  showHero?: boolean
  showCategories?: boolean
  showCollections?: boolean
  showScentQuiz?: boolean
  showJournalTeasers?: boolean
  showPressMarquee?: boolean
  showTestimonials?: boolean
  showNewsletter?: boolean
}

export type SiteSettings = {
  storefront?: StorefrontSettings
  content_visibility?: ContentVisibility
  hero?: HeroSettings
  about?: AboutSettings
  quiz?: QuizSettings
  testimonials?: TestimonialsSettings
  press?: PressSettings
  stats?: StatsSettings
  marquee?: MarqueeSettings
  brand?: BrandSettings
  nav_categories?: NavCategory[]
  promo_modules?: PromoModules
}

const STORAGE_KEY = 'little-sky-settings-v1'

const defaultAboutCards: AboutCard[] = [
  { title: 'Cruelty-Free', body: 'No animal testing at any stage of formulation or production.' },
  { title: 'Refillable', body: '50ml and 100ml sizes are designed to be refilled, reducing glass waste.' },
  { title: 'Sample First', body: 'We encourage sampling before committing. Every sample credit applies to a full bottle.' },
]

const defaultBrand = {
  instagram: 'https://instagram.com/littlesky',
  twitter: '',
  facebook: '',
  email: 'hello@littlesky.com',
  phone: '',
  address: '',
  footerTagline: 'Fine fragrances, made to be discovered. Sample first, find your signature, refill forever.',
}

const defaultTestimonials: Testimonial[] = []

const defaultPressNames = ['Vogue', 'GQ', 'Elle', 'Byrdie', "Harper's Bazaar", 'Allure']

const defaultStats: StatItem[] = [
  { value: '50k+', label: 'Bottles Shipped' },
  { value: '4.9★', label: 'Average Rating' },
  { value: '100%', label: 'Cruelty-Free' },
  { value: 'FSC', label: 'Packaging' },
]

const defaultMarqueeItems = ['fine fragrances', 'sample first', 'refillable', 'cruelty-free', 'made to discover']

const defaultNavCategories: NavCategory[] = [
  { key: 'shop', label: 'Shop', href: '/shop', visible: true },
  { key: 'new', label: 'New', href: '/shop?tag=new', visible: true },
  { key: 'bestsellers', label: 'Bestsellers', href: '/shop?tag=bestseller', visible: true },
  { key: 'collections', label: 'Collections', href: '/collections', visible: true },
  { key: 'quiz', label: 'Scent Quiz', href: '/scent-quiz', visible: true },
  { key: 'gifts', label: 'Gifts', href: '/shop?tag=gift', visible: true },
]

const defaultPromoModules: PromoModules = {
  banner: { enabled: false, text: '', link: '', style: 'pill', background: '#e85d04' },
  streak: { enabled: false, headline: '', body: '', ctaLabel: 'Sign in to start' },
  midnightMarket: {
    enabled: false,
    title: 'Midnight market',
    subtitle: 'A quiet window of limited picks, refreshed nightly.',
    startHour: 0,
    endHour: 6,
    productSlugs: [],
  },
}

const defaultSettings: SiteSettings = {
  storefront: {
    heroHeadline: 'little sky',
    heroSubheadline: 'Perfumes for details',
    featuredQuote: 'Fragrance is the most intense form of memory.',
    announcementText: 'Free shipping on orders over ₦30,000',
    freeShippingThresholdKobo: 3_000_000,
  },
  content_visibility: {
    showScentQuiz: true,
    showJournalTeasers: true,
    showPressMarquee: true,
    showTestimonials: true,
  },
  hero: {
    showFilmButton: true,
    filmButtonText: 'Watch film',
  },
  about: {
    eyebrow: 'The House',
    title: 'little sky',
    paragraphs: [
      'little sky was founded on a simple belief: buying fragrance should feel like discovery, not guesswork. We make fine, modern perfumes and help people find the ones that fit their skin, their mood, and their life.',
      'Our formulas are composed in small batches with a focus on clarity. Each fragrance is built around a clear idea — a place, a memory, a temperature — so the scent story is easy to understand and easy to wear.',
      'We ship samples with every order, credit sample costs toward full bottles, and offer refills for our larger sizes. The goal is less waste, more joy, and a wardrobe of scents you actually use.',
    ],
    cards: defaultAboutCards,
  },
  brand: defaultBrand,
  testimonials: { testimonials: defaultTestimonials },
  press: { names: defaultPressNames },
  stats: { stats: defaultStats },
  marquee: { items: defaultMarqueeItems },
  nav_categories: defaultNavCategories,
  promo_modules: defaultPromoModules,
}

function loadCached(): SiteSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as SiteSettings) : defaultSettings
  } catch {
    return defaultSettings
  }
}

function mergeSettings(remote: SiteSettings): SiteSettings {
  return {
    storefront: { ...defaultSettings.storefront, ...remote.storefront },
    content_visibility: { ...defaultSettings.content_visibility, ...remote.content_visibility },
    hero: { ...defaultSettings.hero, ...remote.hero },
    about: {
      ...defaultSettings.about,
      ...remote.about,
      cards: remote.about?.cards?.length ? remote.about.cards : defaultSettings.about?.cards,
      paragraphs: remote.about?.paragraphs?.length ? remote.about.paragraphs : defaultSettings.about?.paragraphs,
    },
    quiz: { ...defaultSettings.quiz, ...remote.quiz },
    brand: { ...defaultSettings.brand, ...remote.brand },
    testimonials: {
      testimonials: remote.testimonials?.testimonials?.length
        ? remote.testimonials.testimonials
        : defaultSettings.testimonials?.testimonials,
    },
    press: {
      names: remote.press?.names?.length ? remote.press.names : defaultSettings.press?.names,
    },
    stats: {
      stats: remote.stats?.stats?.length ? remote.stats.stats : defaultSettings.stats?.stats,
    },
    marquee: {
      items: remote.marquee?.items?.length ? remote.marquee.items : defaultSettings.marquee?.items,
    },
    nav_categories: remote.nav_categories?.length
      ? remote.nav_categories.map((n) => ({ ...defaultNavCategories.find((d) => d.key === n.key), ...n }))
      : defaultSettings.nav_categories,
    promo_modules: remote.promo_modules
      ? { ...defaultPromoModules, ...remote.promo_modules }
      : defaultSettings.promo_modules,
  }
}

export function useSettings(): SiteSettings {
  const [settings, setSettings] = useState<SiteSettings>(loadCached)

  useEffect(() => {
    fetch(`${API_BASE}/v1/settings`, { headers: { accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { success?: boolean; settings?: SiteSettings } | null) => {
        if (data?.success && data.settings) {
          const merged = mergeSettings(data.settings)
          setSettings(merged)
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
          } catch {}
        }
      })
      .catch(() => {})
  }, [])

  return settings
}

export const SCENT_FAMILIES: ScentFamily[] = ['Citrus', 'Floral', 'Woody', 'Oriental', 'Fresh', 'Gourmand']
